import moment from "moment-timezone";
import { createAction } from "redux-actions";
import humps from "humps";
import { apiGet } from "util/api";
import { handleSoloRecall } from "util/solo";
import Immutable from "immutable";
import * as types from "./constants";
import confirmPropsSelector from "./selectors";
// eslint-disable-next-line import/no-cycle
import { soloFilesToAnnotateInvalidate } from "../annotate/actions";

export const soloConfirmFilesInvalidate = createAction(
  types.SOLO_CONFIRM_FILES_INVALIDATE
);

export const soloConfirmFilesFetchRequest = createAction(
  types.SOLO_CONFIRM_FILES_FETCH_REQUEST
);
export const soloConfirmFilesFetchSuccess = createAction(
  types.SOLO_CONFIRM_FILES_FETCH_SUCCESS
);
export const soloConfirmFilesFetchError = createAction(
  types.SOLO_CONFIRM_FILES_FETCH_ERROR
);
export const soloAddFileToConfirm = createAction(
  types.SOLO_ADD_FILE_TO_CONFIRM
);
export const soloRemoveFileToConfirm = createAction(
  types.SOLO_REMOVE_FILE_TO_CONFIRM
);
export const soloAddFileToRecall = createAction(types.SOLO_ADD_FILE_TO_RECALL);
export const soloRemoveFileToRecall = createAction(
  types.SOLO_REMOVE_FILE_TO_RECALL
);
export const soloConfirmCastsFetchRequest = createAction(
  types.SOLO_CONFIRM_CASTS_FETCH_REQUEST
);
export const soloConfirmCastsFetchSuccess = createAction(
  types.SOLO_CONFIRM_CASTS_FETCH_SUCCESS
);
export const soloConfirmCastsFetchError = createAction(
  types.SOLO_CONFIRM_CASTS_FETCH_ERROR,
  x => x,
  (x, m) => m
);
export const soloRecallFilesRequest = createAction(
  types.SOLO_RECALL_FILES_REQUEST
);
export const soloRecallFilesSuccess = createAction(
  types.SOLO_RECALL_FILES_SUCCESS
);
export const soloRecallFilesError = createAction(
  types.SOLO_RECALL_FILES_ERROR,
  x => x,
  (x, m) => m
);

export const setSoloConfirmStartDateFilter = createAction(
  types.SET_SOLO_CONFIRM_START_DATE_FILTER
);

export const setSoloConfirmEndDateFilter = createAction(
  types.SET_SOLO_CONFIRM_END_DATE_FILTER
);

export const setSoloConfirmInstrumentNumFilter = createAction(
  types.SET_SOLO_CONFIRM_INSTRUMENT_NUMBER_FILTER
);

export const clearSoloFilters = createAction(types.SOLO_CLEAR_FILTERS);

export const updateSoloFilters = createAction(types.UPDATE_SOLO_FILTERS);

export const createUrlPayload = queryString => {
  const urlParams = new URLSearchParams(queryString);
  const payloadObj = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of urlParams.entries()) {
    if (key.includes("Date")) {
      payloadObj[key] = Immutable.fromJS(moment(value));
    } else {
      payloadObj[key] = Immutable.fromJS(value);
    }
  }
  return payloadObj;
};

export const updateFiltersFromQuery = queryString => dispatch => {
  const payloadObj = createUrlPayload(queryString);
  const action = updateSoloFilters(payloadObj);
  dispatch(action);
};

const getSoloConfirmFileFilterQueryString = state => {
  const {
    soloConfirmInstrumentNumFilter,
    workarea,
    organization,
    soloConfirmStartDateFilter,
    soloConfirmEndDateFilter
  } = confirmPropsSelector(state);

  const querys = [`work_area=${workarea}`, `organization=${organization}`];

  if (soloConfirmInstrumentNumFilter !== "any")
    querys.push(`device_sn=${soloConfirmInstrumentNumFilter}`);

  const isADate = str => moment(str, "YYYY-MM-DD", true).isValid();
  if (soloConfirmStartDateFilter) {
    if (isADate(soloConfirmStartDateFilter)) {
      const parsedStartDate = moment(soloConfirmStartDateFilter)
        .tz("America/Los_Angeles")
        .toISOString();
      querys.push(`collection_dt>=${parsedStartDate}`);
    }
  }
  if (soloConfirmEndDateFilter) {
    if (isADate(soloConfirmEndDateFilter)) {
      const parsedEndDate = moment(soloConfirmEndDateFilter)
        .add(1, "days")
        .tz("America/Los_Angeles")
        .toISOString();
      querys.push(`collection_dt<${parsedEndDate}`);
    }
  }
  return querys.join("&");
};

// ASYNC ACTIONS FOR FETCHING FILES TO CONFIRM ANNOTATION
const soloConfirmFilesFetch = () => (dispatch, getState) => {
  const state = getState();
  const filterQuery = getSoloConfirmFileFilterQueryString(state);
  let limit = "limit=50";

  if (
    filterQuery.includes("collection_dt") ||
    filterQuery.includes("device_sn")
  ) {
    limit = "limit=200";
  }

  dispatch(soloConfirmFilesFetchRequest());

  apiGet(
    `/solo/views/solo_confirm_files?${filterQuery}&annotated_date!=null&sort=-annotated_date&${limit}`
  )
    .then(res => res.json())
    .then(humps.camelizeKeys)
    .then(json => dispatch(soloConfirmFilesFetchSuccess(json)))
    .catch(err => dispatch(soloConfirmFilesFetchError(err)));
};
export const soloConfirmFilesFetchIfNeeded = () => (dispatch, getState) => {
  const state = getState();
  const {
    soloConfirmFiles,
    soloConfirmFilesDidInvalidate,
    soloConfirmFilesFetching
  } = confirmPropsSelector(state);
  if (
    (Object.keys(soloConfirmFiles) <= 0 || soloConfirmFilesDidInvalidate) &&
    !soloConfirmFilesFetching
  ) {
    dispatch(soloConfirmFilesFetch());
  }
};

// ASYNC ACTIONS FOR FETCHING CASTS
const soloConfirmCastsFetch = ({ soloFilePk }) => (dispatch, getState) => {
  const { workarea, organization } = confirmPropsSelector(getState());
  dispatch(soloConfirmCastsFetchRequest({ soloFilePk }));
  apiGet(
    `/solo/views/solo_confirm_casts?work_area=${workarea}&organization=${organization}&solo_file_pk=${soloFilePk}&limit=-1`
  )
    .then(res => res.json())
    .then(humps.camelizeKeys)
    .then(json => dispatch(soloConfirmCastsFetchSuccess(json)))
    .catch(err => dispatch(soloConfirmCastsFetchError(err)));
};
export const soloConfirmCastsFetchIfNeeded = ({ soloFilePk }) => (
  dispatch,
  getState
) => {
  const state = getState();
  const {
    soloRecallingFiles,
    soloFetchedFilePks,
    soloConfirmFiles
  } = confirmPropsSelector(state);
  // if not in the middle of recalling files, if the file has at least one cast and isn't in solofetchedfilePks
  if (
    !soloRecallingFiles &&
    !soloFetchedFilePks.includes(soloFilePk) &&
    soloConfirmFiles[soloFilePk].numCasts > 0
  ) {
    dispatch(soloConfirmCastsFetch({ soloFilePk }));
  }
};

// ASYNC ACTION TO RECALL THE FILES IN THE DATABASE
export const soloRecallFiles = () => (dispatch, getState) => {
  const { soloFilesToRecall: soloFilePks, workarea } = confirmPropsSelector(
    getState()
  );
  soloFilePks.forEach(soloFilePk => {
    Promise.resolve(dispatch(soloRecallFilesRequest()))
      .then(() => handleSoloRecall({ soloFilePk }, workarea))
      .then(() => dispatch(soloRecallFilesSuccess({ soloFilePk })))
      .then(() => dispatch(soloConfirmFilesInvalidate()))
      .then(() => dispatch(soloFilesToAnnotateInvalidate()))
      .catch(err => dispatch(soloRecallFilesError(err, { soloFilePk })));
  });
};
